import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/home/CtaBand";
import { Eyebrow, Reveal } from "../components/motion";
import { IMAGES, STORY, TEAM } from "../data/content";
import { TeamPortrait } from "../components/TeamPortrait";
import { Founder } from "../components/home/Founder";

export default function APropos() {
  const navigate = useNavigate();

  const goEquipe = () => navigate("/", { state: { scrollTo: "equipe" } });

  return (
    <main data-testid="page-a-propos">
      <Seo
        title="À propos — Nous n'enseignons pas seulement l'immobilier. Nous le pratiquons. | Demoiselle Atypique"
        description="Demoiselle Atypique est née de l'expérience terrain : nous imaginons, créons et exploitons nous-mêmes des hébergements atypiques. Accompagnement en investissement immobilier atypique."
      />

      <section className="px-6 pt-40 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1500px]">
          <Eyebrow>À propos</Eyebrow>
          <h1 className="mt-6 max-w-5xl font-serif text-5xl leading-[1.06] text-cream sm:text-6xl lg:text-7xl">
            Nous n'enseignons pas seulement l'immobilier.{" "}
            <em className="italic text-neon-light">Nous le pratiquons.</em>
          </h1>
        </div>
      </section>

      <section className="px-6 py-16 sm:px-10 lg:px-16 lg:py-24">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12">
          <div className="space-y-6 leading-relaxed text-white/60 lg:col-span-5">
            <Reveal>
              <p className="text-lg text-white/80">Demoiselle Atypique est née de l'expérience terrain.</p>
            </Reveal>
            <Reveal delay={0.06}>
              <p>Nous avons nous-mêmes imaginé, créé et exploité des hébergements atypiques.</p>
            </Reveal>
            <Reveal delay={0.12}>
              <p>
                Notre philosophie est simple :{" "}
                <span className="text-cream">transmettre ce que nous appliquons réellement sur le terrain.</span>
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p>
                Nous voulons accompagner des investisseurs qui souhaitent sortir des modèles classiques et construire
                des projets avec une véritable identité.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <button onClick={() => navigate("/rendez-vous")} data-testid="apropos-cta" className="btn-primary mt-6">
                Parler de mon projet <ArrowRight className="h-4 w-4" />
              </button>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 gap-5 lg:col-span-7">
            <Reveal className="col-span-2">
              <img
                src={IMAGES.apropos1}
                alt="Love room atypique avec néons et canapé — univers Demoiselle Atypique (photo de nos réalisations)"
                className="aspect-[21/9] w-full object-cover"
              />
            </Reveal>
            <Reveal delay={0.08}>
              <img
                src={IMAGES.apropos2}
                alt="Love room « La Coquette » — lit à baldaquin et néon, création atypique Demoiselle Atypique"
                className="aspect-[4/3] w-full object-cover"
              />
            </Reveal>
            <Reveal delay={0.16}>
              <img
                src={IMAGES.apropos3}
                alt="Love room romantique avec jacuzzi rond et ambiance feutrée — réalisation atypique Demoiselle Atypique"
                className="aspect-[4/3] w-full object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <Founder />

      <section className="border-y border-white/10 bg-[#1D1327] py-24 lg:py-32" data-testid="section-histoire">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Eyebrow>{STORY.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">{STORY.title}</h2>
          </Reveal>
          <div className="mt-12 grid gap-10 lg:grid-cols-2">
            {STORY.blocks.map((block, i) => (
              <Reveal key={i} delay={0.1 + i * 0.06}>
                <p className="border-l-2 border-neon/40 pl-6 leading-relaxed text-white/65">{block}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Eyebrow>L'équipe</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-[1.1] text-cream sm:text-5xl">
              Une équipe aux compétences complémentaires.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-12 flex flex-wrap gap-5">
              {TEAM.map((m) => (
                <figure key={m.id} className="group w-36" data-testid={`apropos-team-${m.id}`}>
                  <div className="aspect-[3/4] w-full overflow-hidden">
                    <TeamPortrait member={m} />
                  </div>
                  <figcaption className="mt-3">
                    <p className="font-serif text-lg text-cream">{m.prenom}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neon">{m.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <button onClick={goEquipe} data-testid="apropos-link-equipe" className="btn-outline mt-12">
              Découvrir l'équipe <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
